import axios from 'axios';

import { getAuthHeader } from '../utils/authUtils';

// API yanıt tipi
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Tablo tipi
export interface DatabaseTable {
  tableName: string;
  schema: string;
  columnCount: number;
  rowCount: number;
  objectType?: 'TABLE' | 'VIEW'; // Nesne tipi: Tablo veya View
}

// Sorgu sonucu tipi
export interface QueryResult {
  columns: string[];
  rows: Record<string, any>[];
  rowCount: number;
  executionTime: number;
}

// Sorgu isteği tipi
export interface QueryRequest {
  databaseId: string;
  query: string;
}

/**
 * Veritabanı SQL işlemleri için API servisi
 */
class DatabaseQueryService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }
  
  /**
   * Veritabanlarını listeler
   * @returns Veritabanı listesi
   */
  async listDatabases(): Promise<ApiResponse<any[]>> {
    try {
      const response = await axios.get<ApiResponse<any[]>>(
        `${this.apiUrl}/api/v1/DatabaseQuery/databases`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Veritabanları alınırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Veritabanları alınırken hata oluştu',
        data: []
      };
    }
  }

  /**
   * Veritabanı tablolarını listeler
   * @param databaseId Veritabanı ID
   * @returns Tablo listesi
   */
  async getTables(databaseId: string): Promise<ApiResponse<DatabaseTable[]>> {
    try {
      const response = await axios.get<ApiResponse<DatabaseTable[]>>(
        `${this.apiUrl}/api/v1/DatabaseQuery/${databaseId}/tables`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Tablolar alınırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Tablolar alınırken hata oluştu',
        data: []
      };
    }
  }

  /**
   * SQL sorgusu çalıştırır
   * @param databaseId Veritabanı ID
   * @param query SQL sorgusu
   * @returns Sorgu sonucu
   */
  async executeQuery(databaseId: string, query: string): Promise<ApiResponse<QueryResult>> {
    try {
      const response = await axios.post<ApiResponse<QueryResult>>(
        `${this.apiUrl}/api/v1/DatabaseQuery/execute`,
        { databaseId, query },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error('SQL sorgusu çalıştırılırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'SQL sorgusu çalıştırılırken hata oluştu',
        data: { columns: [], rows: [], rowCount: 0, executionTime: 0 }
      };
    }
  }

  /**
   * Veritabanı tablolarını ve viewlerini listeler
   * @param databaseId Veritabanı ID
   * @returns Tablo ve view listesi
   */
  async getTablesAndViews(databaseId: string): Promise<ApiResponse<DatabaseTable[]>> {
    try {
      console.log(`Tablolar ve viewler getiriliyor, databaseId: ${databaseId}`);
      
      // Önce tabloları alalım
      const tablesResponse = await this.getTables(databaseId);
      let allObjects: DatabaseTable[] = [];
      
      if (tablesResponse.success && tablesResponse.data.length > 0) {
        // Tablolara objectType ekleyelim
        allObjects = tablesResponse.data.map(table => ({
          ...table,
          objectType: 'TABLE'
        }));
      }
      
      // Şimdi viewleri almak için özel bir sorgu çalıştıralım
      try {
        const viewQuery = `
          SELECT 
            TABLE_SCHEMA as schema, 
            TABLE_NAME as tableName,
            0 as columnCount,
            0 as rowCount
          FROM INFORMATION_SCHEMA.VIEWS
          WHERE TABLE_CATALOG = DB_NAME()
          ORDER BY TABLE_SCHEMA, TABLE_NAME
        `;
        
        const viewsResponse = await this.executeQuery(databaseId, viewQuery);
        
        if (viewsResponse.success && viewsResponse.data.rows.length > 0) {
          // Viewleri dönüştürelim ve listeye ekleyelim
          const views: DatabaseTable[] = viewsResponse.data.rows.map((row: any) => ({
            schema: row.schema,
            tableName: row.tableName,
            columnCount: row.columnCount || 0,
            rowCount: row.rowCount || 0,
            objectType: 'VIEW'
          }));
          
          allObjects = [...allObjects, ...views];
        }
      } catch (viewError) {
        console.error('Viewler alınırken hata oluştu:', viewError);
        // Viewler alınamazsa sadece tabloları döndürmeye devam edelim
      }
      
      return {
        success: true,
        message: 'Tablolar ve viewler başarıyla listelendi',
        data: allObjects
      };
    } catch (error: any) {
      console.error('Tablolar ve viewler alınırken hata oluştu:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Tablolar ve viewler alınırken hata oluştu',
        data: []
      };
    }
  }
}

export default new DatabaseQueryService();
